import { memo, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import {
    BottomNavigation,
    BottomNavigationTabsHome,
    TabsParent,
} from "@swiggy-private/dls-web-core/dist/components/BottomNavigation";
import {
    AnalyticsContext,
    BottomBarContext,
    ResponseContext,
    sendEvent,
    TableContext,
    useDigitalMenuConfig,
} from "@swiggy-private/portal-dineout-context";
import { Overlay } from "@swiggy-private/portal-dineout-ui";
import { useRouter } from "next/compat/router";
import { getHideBottomBarFromStorage } from "utils/sessionFlags";
import { OBJECT_NAMES } from "./constants";
import { getAnalyticsData, getShownTabs } from "./helpers";
import { BottomContainer } from "./styled";
import { ROUTES } from "../../constants";
import { useIsV2InApp, useBillPayment, IBillPaymentResponse } from "../../hooks";
import { TabType } from "../../types/cart";
import BillPaymentHalfCardRenderer from "../BillPaymentHalfCard/BillPaymentHalfCardRenderer";
import { withPinAuth } from "../PinAuth";

const BottomBar = () => {
    const router = useRouter();
    const { isVisible, setIsVisible, shouldFetchPartnerOrder } = useContext(BottomBarContext);
    const { isV2InApp } = useIsV2InApp();
    const { clickEvent, impressionEvent } = useContext(AnalyticsContext);
    const tableContext = useContext(TableContext);
    const { state } = useContext(ResponseContext);
    const { tableNo } = state?.pin?.data || {};
    const [showOverlay, setShowOverlay] = useState<boolean>(false);
    const [billPaymentData, setBillPaymentData] = useState<IBillPaymentResponse | null>(null);
    const { billPayment } = useBillPayment();
    const visitedTabsRef = useRef<TabsParent[]>([]);
    const { config } = useDigitalMenuConfig();

    const handleTabImprssion = useCallback(
        (tab: TabsParent) => {
            if (!isVisible || visitedTabsRef.current.includes(tab)) return;

            visitedTabsRef.current.push(tab);
            const analyticsData = getAnalyticsData({
                tab,
                route: router?.route || "",
                objectName: OBJECT_NAMES.IMPRESSION_TAB,
                tableNo: tableNo || "",
                tableContext,
                config,
            });
            sendEvent?.({ ...analyticsData, eventFn: impressionEvent });
        },
        [isVisible, tableNo, router?.route, tableContext, config, impressionEvent],
    );

    const handleClickAnalytics = useCallback(
        (tab: TabsParent, index: number) => {
            const analyticsData = getAnalyticsData({
                tab,
                index,
                route: router?.route || "",
                objectName: OBJECT_NAMES.CLICK_TAB,
                tableNo: tableNo || "",
                tableContext,
                config,
            });
            sendEvent?.({ ...analyticsData, eventFn: clickEvent });
        },
        [router?.route, tableContext, tableNo, config, clickEvent],
    );

    const isOrdersTab = (tab: TabsParent): boolean => {
        return tab === BottomNavigationTabsHome.MY_ORDERS || tab === BottomNavigationTabsHome.MY_ORDERS_NOTIFICATION;
    };

    const onClickTab = async (tab: TabsParent) => {
        if (tab === BottomNavigationTabsHome.MENU) {
            handleClickAnalytics(tab, 1);
            router?.push(ROUTES.MENU);
            return;
        }
        if (tab === BottomNavigationTabsHome.SEARCH) {
            handleClickAnalytics(tab, 2);
            router?.push(ROUTES.SEARCH);
            return;
        }
        if (isOrdersTab(tab)) {
            handleClickAnalytics(tab, 3);
            router?.push(`${ROUTES.CART_ORDERS}?tab=${TabType.ORDERS}`);
            return;
        }
        if (tab === BottomNavigationTabsHome.PAYBILL) {
            try {
                setShowOverlay(true);
                handleClickAnalytics(tab, 4);
                const result = await billPayment();
                setShowOverlay(false);
                if (result) {
                    setBillPaymentData(result);
                }
            } catch (_error) {
                // handling error in billPayment hook
                setShowOverlay(false);
                return;
            }
            return;
        }
        return;
    };

    const getCurrentSelectedTab: () => BottomNavigationTabsHome = () => {
        if (router?.route.includes(ROUTES.SEARCH)) {
            return BottomNavigationTabsHome.SEARCH;
        }
        if (router?.route.includes(ROUTES.MENU)) {
            return BottomNavigationTabsHome.MENU;
        }

        // Helper to determine orders tab based on partner order status
        const getOrdersTab = () =>
            shouldFetchPartnerOrder
                ? BottomNavigationTabsHome.MY_ORDERS_NOTIFICATION
                : BottomNavigationTabsHome.MY_ORDERS;

        // Support new unified cart-orders route
        if (router?.route.includes(ROUTES.CART_ORDERS)) {
            const { tab } = router.query;
            if (tab === TabType.ORDERS) {
                return getOrdersTab();
            }
        }
        // Keep existing track route for backward compatibility
        if (router?.route.includes(ROUTES.TRACK)) {
            return getOrdersTab();
        }
        return BottomNavigationTabsHome.REORDER;
    };

    const selectedTab = getCurrentSelectedTab();
    const shownTabs = useMemo(() => getShownTabs(config, shouldFetchPartnerOrder), [config, shouldFetchPartnerOrder]);
    const strokeColor = useMemo(() => (isOrdersTab(selectedTab) ? "primary" : undefined), [selectedTab]);

    useEffect(() => {
        const hideBottomBar = getHideBottomBarFromStorage();
        if (hideBottomBar) {
            setIsVisible(false);
        }
    }, [setIsVisible, isVisible]);

    if (!isVisible || isV2InApp) return null;

    return (
        <BottomContainer id="bottom-bar">
            <BottomNavigation
                shownTabs={shownTabs}
                selectedTab={selectedTab}
                onClickTab={onClickTab}
                customPadding={{
                    top: 16,
                    right: 60,
                    bottom: "calc(var(--sab, 0px) + 24px)",
                    left: 60,
                }}
                iconSize={{
                    width: "24",
                    height: "24",
                }}
                strokeColor={strokeColor}
                fillColor="primary"
                onTabImpression={handleTabImprssion}
            />
            {showOverlay && <Overlay />}
            {billPaymentData && (
                <BillPaymentHalfCardRenderer
                    billPaymentData={billPaymentData}
                    onClose={() => setBillPaymentData(null)}
                />
            )}
        </BottomContainer>
    );
};

export default memo(withPinAuth(BottomBar, { shouldAutoGeneratePin: false }));
